import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Reusable skeleton building blocks.
 * Compose these into page-level skeletons.
 */

export function SkeletonText({
  lines = 1,
  widths,
  className,
}: {
  lines?: number;
  /** Per-line widths, e.g. ["100%", "80%", "60%"]. Cycles if shorter than lines. */
  widths?: string[];
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-4"
          style={{ width: widths?.[i % widths.length] ?? "100%" }}
        />
      ))}
    </div>
  );
}

export function SkeletonAvatar({ className }: { className?: string }) {
  return <Skeleton className={cn("h-10 w-10 rounded-full", className)} />;
}

export function SkeletonBadge({ className }: { className?: string }) {
  return <Skeleton className={cn("h-6 w-16 rounded-full", className)} />;
}

export function SkeletonButton({ className }: { className?: string }) {
  return <Skeleton className={cn("h-10 w-24", className)} />;
}

export function SkeletonCard({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-outline-variant/10 bg-surface-container-lowest p-6 shadow-card dark:bg-surface-container",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SkeletonStatCard({ className }: { className?: string }) {
  return (
    <SkeletonCard className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-8 rounded-lg" />
      </div>
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-3 w-20" />
    </SkeletonCard>
  );
}

export function SkeletonTableRow({
  columns = 5,
  className,
}: {
  columns?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-4 border-b border-outline-variant/10 px-6 py-4",
        className,
      )}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-4 flex-1"
          style={{ maxWidth: i === 0 ? "30%" : undefined }}
        />
      ))}
    </div>
  );
}

export function SkeletonTable({
  rows = 8,
  columns = 5,
}: {
  rows?: number;
  columns?: number;
}) {
  return (
    <SkeletonCard className="p-0 overflow-hidden">
      {/* Table header */}
      <div className="flex items-center gap-4 border-b border-outline-variant/20 bg-surface-container-low/50 px-6 py-3">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton
            key={i}
            className="h-3 flex-1"
            style={{ maxWidth: i === 0 ? "30%" : undefined }}
          />
        ))}
      </div>
      {/* Table rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonTableRow key={i} columns={columns} />
      ))}
    </SkeletonCard>
  );
}

export function SkeletonFormField({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-2", className)}>
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

export function SkeletonPageHeader({
  withActions = true,
}: {
  withActions?: boolean;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
      </div>
      {withActions && (
        <div className="flex gap-2">
          <SkeletonButton className="w-28" />
          <SkeletonButton className="w-28" />
        </div>
      )}
    </div>
  );
}

export function SkeletonFilterBar({
  fields = 3,
}: {
  fields?: number;
}) {
  return (
    <SkeletonCard className="mb-6 p-4">
      <div className="flex flex-wrap items-end gap-4">
        <Skeleton className="h-10 flex-1 min-w-[200px]" />
        {Array.from({ length: fields }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-40" />
        ))}
      </div>
    </SkeletonCard>
  );
}

export function SkeletonChart({ className }: { className?: string }) {
  return (
    <SkeletonCard className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-8 w-24" />
      </div>
      <div className="flex h-64 items-end gap-2">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton
            key={i}
            className="flex-1"
            style={{ height: `${30 + ((i * 37) % 70)}%` }}
          />
        ))}
      </div>
    </SkeletonCard>
  );
}
